import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import Text from "../../../atoms/Text";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { reorderSectionsByPromptType } from "../../../../utils/reviewContentUtils";
import { reviewContentTransform } from "../../../../utils/reviewContentTransform";
import { ReviewSectionContainer, ReviewSectionBody, DetailSection, Badge, ConsultBadge, EvidenceBadge } from "./styles";
import ReferenceButton from "./components/ReferenceButton";
import HospitalButton from "./components/HospitalButton";
import PrescriptionButton from "./components/PrescriptionButton";
import type { ReferenceMode } from "./index";
import { Drug, DrugSearchResult } from "../../../../common/models/types";
import DrugImage from "../../../molecules/DrugImage";
import { extractItemCode } from "../../../../utils/drugImageUtils";
import { parseSectionContent } from "../../../../utils/sectionParser";
import { extractReactNodeText } from "../../../../utils/reactNodeText";
import type { HospitalAliasMap } from "../../../../utils/extractDepartment";

/**
 * rehype-raw 경유 원시 HTML의 실행·외부 로드 벡터 최소 방어(계획 §5-1 · 2차 검토).
 * 비인증 공개 페이지(탑재뷰)에서도 이 렌더러를 공유하므로 건다. `disallowedElements`는 hast 트리
 * 전역에서 태그명으로 제거하므로 중첩된 <svg><script>도 함께 제거된다. 정상 보고서엔 이 태그가
 * 나올 일이 없어 3단·출력미리보기 렌더 결과는 바뀌지 않는다.
 */
const DISALLOWED_ELEMENTS = ["script", "iframe", "style", "form", "object", "embed", "link", "base"];

interface MarkdownViewerProps {
  content: string;
  promptType?: string;
  onReferenceClick?: (referenceId: string) => void;
  referenceMode?: ReferenceMode;
  drugsData?: Drug[];
  pillsData?: DrugSearchResult[];
  onDrugImageClick?: (itemCode: string) => void;
  sectionVisibility?: {
    hidePharmacistReport: boolean;
    hidePatientGuide: boolean;
  };
  // 병원 버튼화(spec Phase 3·4) — 전부 optional(파생 소비자 컴파일 불변).
  //   선택 상태(highlightedHospital)는 본문에 표시하지 않으므로 통로 없음(spec 설계 결정 13).
  hospitalAliasMap?: HospitalAliasMap;
  onHospitalClick?: (alias: string) => void;
  // 처방전 지칭 버튼화(spec 설계 결정 11).
  onPrescriptionClick?: (n: number) => void;
  prescriptionGroupCount?: number;
}

// 처방전 지칭 버튼화 컨텍스트(spec 설계 결정 11) — count 이하 N만 버튼(화이트리스트).
type PrescriptionCtx = {
  count: number;
  onPrescriptionClick?: (n: number) => void;
};

// 배지 및 이미지 처리를 위한 함수
const processTextNode = (
  text: string,
  drugsData?: Drug[],
  pillsData?: DrugSearchResult[],
  onDrugImageClick?: (itemCode: string) => void,
  sectionVisibility?: { hidePharmacistReport: boolean; hidePatientGuide: boolean },
  prescriptionCtx?: PrescriptionCtx
) => {
  const parts = text.split(/(⑆상담필요|⑆근거확인필요|⑆img_[A-Za-z0-9]+|처방전\d+|\[약사용 보고서\]|\[환자용 복약지도문?\]|\[종사자용 안내문\])/g);

  // 첫 번째로 표시되는 섹션 마커 집합 (이 안에 들면 페이지 나눔 없음)
  // 종사자용 안내문은 환자용 복약지도문을 대체하므로 동일하게 patient-side로 취급
  const firstVisibleMarkers = !sectionVisibility?.hidePharmacistReport
    ? ['[약사용 보고서]']
    : ['[환자용 복약지도문]', '[환자용 복약지도]', '[종사자용 안내문]'];

  return parts.map((part, index) => {
    if (part === '⑆상담필요') {
      // data-badge: 숨김 렌더에서 extractDrpChecklist가 제거용으로 고른다(화면 영향 없음, spec 설계 결정 5).
      return React.createElement(ConsultBadge, { key: index, 'data-badge': 'consult' }, '상담필요');
    } else if (part === '⑆근거확인필요') {
      return React.createElement(EvidenceBadge, { key: index, 'data-badge': 'evidence' }, '근거확인필요');
    } else if (/^처방전\d+$/.test(part)) {
      // 처방전N → 버튼(그 리뷰의 그룹 수 이하 N만). 화이트리스트 밖·핸들러 없으면 평문.
      const n = parseInt(part.slice(3), 10);
      if (prescriptionCtx && prescriptionCtx.count >= n) {
        return React.createElement(PrescriptionButton, {
          key: index,
          n,
          onPrescriptionClick: prescriptionCtx.onPrescriptionClick,
        });
      }
      return part;
    } else if (part === '[약사용 보고서]' || part === '[환자용 복약지도문]' || part === '[환자용 복약지도]' || part === '[종사자용 안내문]') {
      // 첫 번째 섹션 마커가 아니면 페이지 나눔
      const needsPageBreak = !firstVisibleMarkers.includes(part);

      return React.createElement('div', {
        key: index,
        className: 'section-title',
        'data-page-break': needsPageBreak ? 'true' : 'false',
        style: {
          display: 'block',
          fontWeight: '600',
          margin: '20px 0 12px 0'
        }
      }, part);
    } else if (part.startsWith('⑆img_')) {
      const itemCode = extractItemCode(part);

      return React.createElement(DrugImage, {
        key: index,
        itemCode,
        drugsData,
        pillsData,
        onClick: onDrugImageClick
      });
    }
    return part;
  });
};

const processChildren = (
  node: React.ReactNode,
  drugsData?: Drug[],
  pillsData?: DrugSearchResult[],
  onDrugImageClick?: (itemCode: string) => void,
  sectionVisibility?: { hidePharmacistReport: boolean; hidePatientGuide: boolean },
  prescriptionCtx?: PrescriptionCtx
): React.ReactNode => {
  if (typeof node === 'string') {
    return processTextNode(node, drugsData, pillsData, onDrugImageClick, sectionVisibility, prescriptionCtx);
  }
  if (Array.isArray(node)) {
    return node.map((child, idx) =>
      React.createElement(React.Fragment, { key: idx }, processChildren(child, drugsData, pillsData, onDrugImageClick, sectionVisibility, prescriptionCtx))
    );
  }
  if (React.isValidElement(node)) {
    const element = node as React.ReactElement;
    if (element.props && element.props.children) {
      return React.cloneElement(element, {
        ...element.props,
        children: processChildren(element.props.children, drugsData, pillsData, onDrugImageClick, sectionVisibility, prescriptionCtx)
      });
    }
  }
  return node;
};

// 상세 섹션 분리 함수
const separateDetailSection = (content: string) => {
  const detailStartMatch = content.match(/\{DETAIL_SECTION_START\}/);
  const detailEndMatch = content.match(/\{DETAIL_SECTION_END\}/);

  if (detailStartMatch && detailEndMatch) {
    const startIndex = detailStartMatch.index! + detailStartMatch[0].length;
    const endIndex = detailEndMatch.index!;

    const mainContent = content.substring(0, detailStartMatch.index!) +
                        content.substring(endIndex + detailEndMatch[0].length);
    const detailContent = content.substring(startIndex, endIndex);

    return {
      mainContent,
      detailContent,
      hasDetailSection: true
    };
  }

  return {
    mainContent: content,
    detailContent: '',
    hasDetailSection: false
  };
};

const MarkdownViewer: React.FC<MarkdownViewerProps> = ({
  content,
  promptType,
  onReferenceClick,
  referenceMode = 'button',
  drugsData,
  pillsData,
  onDrugImageClick,
  sectionVisibility,
  hospitalAliasMap,
  onHospitalClick,
  onPrescriptionClick,
  prescriptionGroupCount,
}) => {
  const user = useSelector((state: RootState) => state.user.user);
  const isAdmin = user?.roles?.includes("admin") || false;

  // 처방전 버튼화 컨텍스트(그 리뷰의 그룹 수 화이트리스트 + 클릭).
  const prescriptionCtx: PrescriptionCtx = {
    count: prescriptionGroupCount ?? 0,
    onPrescriptionClick,
  };

  // 프롬프트 타입에 따라 섹션 재배치
  const reorderedText = reorderSectionsByPromptType(content, promptType);

  // 상세 섹션 분리
  const { mainContent, detailContent, hasDetailSection } = separateDetailSection(reorderedText || '');

  // 섹션별 분리
  const sections = parseSectionContent(mainContent);

  // beforeSections가 DRP(약사용 분석요약) 콘텐츠인지 감지
  const isDrpSummary = sections.beforeSections.includes('분석결과 요약') ||
                       sections.beforeSections.includes('{DRP}');

  // 마크다운 변환 (referenceMode + hospitalAliasMap 전달 — 병원 변환이 ref 변환보다 먼저 실행됨)
  const processedBeforeSections = reviewContentTransform.toMarkdown(sections.beforeSections, 'ReviewSector-main', referenceMode, hospitalAliasMap);
  const processedPharmacistReport = sections.pharmacistReport
    ? reviewContentTransform.toMarkdown(sections.pharmacistReport, 'ReviewSector-pharmacist', referenceMode, hospitalAliasMap)
    : '';
  const processedPatientGuide = sections.patientGuide
    ? reviewContentTransform.toMarkdown(sections.patientGuide, 'ReviewSector-patient', referenceMode, hospitalAliasMap)
    : '';
  const processedDetailContent = hasDetailSection
    ? reviewContentTransform.toMarkdown(detailContent, 'ReviewSector-detail', referenceMode, hospitalAliasMap)
    : '';

  const markdownComponents = {
    badge: ({ children }: any) => {
      // 텍스트 추출 — 병원 버튼 등 엘리먼트가 섞여도 재귀로 실제 텍스트를 뽑는다(설계 결정 6).
      // 문자열 children이면 기존 String(children)과 동일 결과.
      const text = extractReactNodeText(children);

      // 페이지 나눔이 필요한 텍스트 패턴
      const needsPageBreak = text.includes('다음 진료시') ||
                             text.includes('다음 진료 시') ||
                             text.includes('선생님께');

      return (
        <mark
          data-color="#1a1a1a"
          data-page-break={needsPageBreak ? 'true' : 'false'}
          style={{
            backgroundColor: '#1a1a1a',
            padding: '4px 14px',
            borderRadius: '15px',
            display: 'inline-block',
            margin: '12px 0 8px 0',
            textDecoration: 'none'
          }}
        >
          <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>
            {children}
          </span>
        </mark>
      );
    },
    h3: ({ children }: any) => <h3>{children}</h3>,
    p: ({ children }: any) => <p>{processChildren(children, drugsData, pillsData, onDrugImageClick, sectionVisibility, prescriptionCtx)}</p>,
    li: ({ children }: any) => <li>{processChildren(children, drugsData, pillsData, onDrugImageClick, sectionVisibility, prescriptionCtx)}</li>,
    a: ({ href, children }: any) => {
      // 0. 병원 링크 처리 (#reference- 분기 옆) — [실명](#hospital-A) 중간형 → HospitalButton.
      if (href && href.startsWith("#hospital-")) {
        const alias = href.replace("#hospital-", "").toUpperCase();
        const entry = hospitalAliasMap?.[alias];
        // 실명: 맵 우선(원본 대괄호 유지), 없으면 렌더된 링크 텍스트(전각 치환본).
        const name = entry?.name ?? extractReactNodeText(children);
        return (
          <HospitalButton
            alias={alias}
            name={name}
            label={entry?.label}
            onHospitalClick={onHospitalClick}
          />
        );
      }

      // 1. 참조 링크 처리 (기존 로직 유지)
      if (href && href.startsWith("#reference-")) {
        const refId = href.replace("#reference-", "");

        // referenceMode가 'none'이면 참조 버튼과 텍스트를 완전히 제거
        if (referenceMode === 'none') {
          return <></>;
        }

        return (
          <ReferenceButton
            refId={refId}
            onReferenceClick={onReferenceClick}
            isAdmin={isAdmin}
          >
            {children}
          </ReferenceButton>
        );
      }

      // 2. 유효한 URL 패턴 검증
      const isValidUrl = href && (
        href.startsWith('http://') ||
        href.startsWith('https://') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('/')
      );

      // 3. 유효한 URL만 링크로 렌더링
      if (isValidUrl) {
        return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
      }

      // 4. 유효하지 않은 URL은 원본 마크다운 형태로 텍스트 출력 (URL 디코딩)
      const decodedHref = (() => {
        try {
          return decodeURIComponent(href);
        } catch {
          return href;
        }
      })();
      return <>[{children}]({decodedHref})</>;
    },
    // 취소선을 일반 텍스트로 변환 (원본 틸드 포함)
    del: ({ children }: any) => {
      return <>~{children}~</>;
    },
  };

  return (
    <ReviewSectionContainer>
      <ReviewSectionBody>
        <Text textType="Body1">
          {/* 공통 내용 (섹션 마커 이전) - DRP인 경우 약사용 보고서 숨김에 연동 */}
          {!(isDrpSummary && sectionVisibility?.hidePharmacistReport) && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              disallowedElements={DISALLOWED_ELEMENTS}
              components={markdownComponents}
            >
              {processedBeforeSections}
            </ReactMarkdown>
          )}

          {/* 약사용 보고서 섹션 */}
          {sections.pharmacistReport && (!sectionVisibility || !sectionVisibility.hidePharmacistReport) && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              disallowedElements={DISALLOWED_ELEMENTS}
              components={markdownComponents}
            >
              {processedPharmacistReport}
            </ReactMarkdown>
          )}

          {/* 환자용 복약지도문 섹션 */}
          {sections.patientGuide && (!sectionVisibility || !sectionVisibility.hidePatientGuide) && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              disallowedElements={DISALLOWED_ELEMENTS}
              components={markdownComponents}
            >
              {processedPatientGuide}
            </ReactMarkdown>
          )}
        </Text>

        {/* 상세 검토 섹션 */}
        {hasDetailSection && (
          <DetailSection>
            <Text textType="Body1">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                disallowedElements={DISALLOWED_ELEMENTS}
                components={markdownComponents}
              >
                {processedDetailContent}
              </ReactMarkdown>
            </Text>
          </DetailSection>
        )}
      </ReviewSectionBody>
    </ReviewSectionContainer>
  );
};

export default MarkdownViewer;